<script lang="ts" setup>
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import { computed, onBeforeMount, ref } from "vue";
import StorySelectionPopup from "@/components/mobile/popup/StorySelectionPopup.vue";
import StoryListItem from "@/components/mobile/story/story-chapter/StoryListItem.vue";
import ToneSelectionPopup from "@/components/mobile/popup/ToneSelectionPopup.vue";
import { useTone } from "@/stores/tone";
import { showConfirmDialog, showLoadingToast } from "vant";
import { useRoute, useRouter } from "vue-router";
import { useStory } from "@/stores/story";
import { useI18n } from "vue-i18n";
import api from "@/modules/api";
import { StoryScenePreviewPopupInstance } from "./CreateStorySceneTaskPreview.vue";
import { ApiDeleteStoryGameDialog } from "@/apis/story-game";
import { useStoryGameEditor } from "@/stores/storyGameEditor";
import { storeToRefs } from "pinia";

const { t } = useI18n();

const previewPopupRef = ref<StoryScenePreviewPopupInstance | null>(null);
const showStorySelection = ref(false);
const selectedStory = ref<Story | null>(null);
const storySceneList = ref<StoryGameDialog[]>([]);
const selectedStoryId = computed(() => {
  return selectedStory.value?.id ?? 0;
});

const toneStore = useTone();
const showToneSelection = ref(false);
// const selectedToneId = ref<number>(0);
const selectedTone = computed(() => {
  return toneStore.getToneById(store.mateToneId);
});

// LifeCycle
const route = useRoute();
const { storyUuid, storyNodeId, storyGameId, storyGameUuid } = route.query;
const storyStore = useStory();
const store = useStoryGameEditor();

const prepareData = async () => {
  toneStore.getBgmList();
  const loading = showLoadingToast({ message: t("Message.loading") });
  try {
    if (storyUuid) {
      const story = await storyStore.getStoryItem(storyUuid as string);
      await store.resetGame({
        id: Number(storyGameId),
        uuid: String(storyGameUuid),
        storyNodeId: Number(storyNodeId),
      });
      selectedStory.value = story;
      store.mateToneId = story.mateToneId || 0;
    }
  } finally {
    loading.close();
  }
};
onBeforeMount(() => {
  prepareData();
});

const clickDeleteStoryScene = (id: number) => {
  showConfirmDialog({
    title: t("ConfirmModal.sureDeleteText"),
    confirmButtonText: t("Buttons.confirm"),
    cancelButtonText: t("Buttons.cancel"),
  }).then(async () => {
    const loading = showLoadingToast({
      message: t("Message.deleting"),
    });
    await ApiDeleteStoryGameDialog(id);
    loading.close();
    store.dialogList = store.dialogList.filter((item) => item !== id);
  });
};

// EventHandler
const router = useRouter();
const onPreview = async (id?: number) => {
  const loading = showLoadingToast({ message: t("Message.loading") });
  await previewPopupRef.value?.startPreview({
    dialogId: id,
    nodeId: Number(storyNodeId),
  });
  loading.close();
};

const clickSort = (id: number, isUp: boolean) => {
  const { dialogList } = store;
  const index = dialogList.findIndex((item) => item === id);
  const targetIndex = isUp ? index - 1 : index + 1;
  const target = dialogList[targetIndex];
  dialogList[targetIndex] = dialogList[index];
  dialogList[index] = target;
  store.sortDialog();
};

const clickEditImg = (dialogId: number) => {
  router.push({
    name: "storyPerformanceEditor",
    query: {
      galGameId: storyGameId,
      dialogId,
      nodeId: storyNodeId,
    },
  });
};

const showEditText = ref(false);
const content = ref("");

const clickEditText = (item: StoryGameDialog) => {
  content.value = item?.text || "";
  store.activeDialog = item;
  showEditText.value = true;
};

const onEditTextConfirm = (text: string) => {
  store.activeDialog.text = text;
  showEditText.value = false;
  store.saveDialog();
};

const onSelectTone = async (mateToneId: number) => {
  const loading = showLoadingToast({
    message: t("Message.saving"),
  });
  store.mateToneId = mateToneId;
  await api.updateOneStory(selectedStoryId.value, {
    mateToneId,
  });
  if (mateToneId === 0) prepareData();
  showToneSelection.value = false;
  loading.close();
};
// const toneStore = useTone();
const { bgmList } = storeToRefs(toneStore);

const bgm = computed(
  () =>
    bgmList.value.find((v) => v.mediaId === store.activeNode?.bgmMediaId)?.name
);

const clickSelectBgm = () => {
  store.bgmType = 0;
  store.showBgmModal = true;
  store.currentBgmId =
    toneStore.getBgmByMediaId(store.activeNode.bgmMediaId!)?.id ?? 0;
};

const onSelectBgm = async (bgmId: number) => {
  store.activeNode.bgmMediaId = useTone().getBgmById(bgmId)!.mediaId;
  store.saveNode();
  store.showBgmModal = false;
};

const clickViewDialog = () => {
  store.activeCharacterId = store.form.narrator.id!;
  store.activeStoryNodeId = undefined;
  store.characterDialogList = [];
  store.showCharacterAllDialogModal = true;
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr]">
    <threads-nav :enable-switch-mate="false" :show-back="true">
      <template #center>
        <div></div>
      </template>
      <template #right>
        <div v-if="store.saveLoading" class="loading loading-ring"></div>
        <button class="btn btn-primary btn-sm rounded" @click="onPreview()">
          {{ $t("Buttons.preview") }}
        </button>
      </template>
    </threads-nav>
    <div class="p-2 flex flex-col gap-4 h-full overflow-y-auto">
      <!-- 选择故事章节 -->
      <div class="flex flex-col gap-2">
        <div class="text-base font-bold">
          {{ $t("story.storyChapter") }}
        </div>
        <story-list-item
          v-if="selectedStory"
          :story="selectedStory"
          class="border border-primary px-2"
        />
      </div>
      <!-- 选择叙述人声音 -->
      <div class="flex flex-col gap-2">
        <div class="text-base font-bold">{{ $t("story.narratorVoice") }}</div>
        <div
          v-if="!selectedTone"
          class="flex flex-col items-center justify-center gap-2 p-4 border border-primary text-base-content/50"
          @click="showToneSelection = true"
        >
          {{ $t("story.aNarratorVoice") }}
          <button class="btn btn-primary btn-sm rounded btn-wide">
            {{ $t("Buttons.select") }}
          </button>
        </div>
        <div
          v-else
          class="flex justify-between items-center border p-2 rounded"
          @click="showToneSelection = true"
        >
          <div class="flex gap-2 items-center">
            {{ selectedTone!.name }}
          </div>
        </div>
      </div>

      <!-- 背景音乐 -->
      <div>
        <div class="flex items-center justify-between mt-3 mb-2">
          <h2 class="text-base font-bold">
            {{ $t("game.base.backgroundMusic") }}
            <span class="text-[#E84B4B]">*</span>
          </h2>
        </div>
        <VanField
          v-model="bgm"
          :placeholder="$t('game.clickChooseBackgroundMusic')"
          class="bg-base-100 border rounded"
          is-link
          name="picker"
          readonly
          required
          @click="clickSelectBgm"
        />
      </div>

      <!-- 正文 -->
      <div class="flex flex-col gap-2">
        <div class="flex justify-between items-center">
          <div class="text-base font-bold">
            {{ $t("story.setting.body") }}
          </div>
          <div class="flex items-center">
            <button
              class="btn btn-primary btn-xs rounded-sm mr-1"
              @click="clickViewDialog"
            >
              {{ $t("game.audioManager") }}
            </button>
          </div>
        </div>

        <div
          v-for="(dialogId, i) in store.dialogList"
          :key="dialogId"
          class="grid grid-cols-[1fr] gap-3 mb-4 px-3"
        >
          <StoryAddDialogBtnList :i="i" />
          <div class="flex-1 border rounded-md p-2.5 flex flex-col">
            <p class="flex-1">
              {{ store.dialogMap[dialogId]?.text }}
              <i
                class="iconify mingcute--edit-3-line p-1 cursor-pointer"
                @click="clickEditText(store.dialogMap[dialogId])"
              />
            </p>

            <!-- 管理 -->
            <div class="flex justify-between mt-4 font-light">
              <div class="flex items-center flex-wrap">
                <button
                  :class="[
                    'btn btn-neutral btn-xs rounded border-none mr-2 px-1',
                    store.dialogMap[dialogId].sceneMediaId && 'text-primary',
                  ]"
                  @click="clickEditImg(dialogId)"
                >
                  <i class="iconify mingcute--pic-line1 text-xs font-light" />
                </button>
                <button
                  class="btn btn-neutral btn-xs rounded border-none mr-2 px-1"
                  @click="onPreview(dialogId)"
                >
                  <i
                    class="iconfont icon-preview text-xs mr-[-3px] font-light"
                  />
                  <span>{{ $t("Buttons.preview") }}</span>
                </button>
              </div>
              <div class="flex items-center flex-wrap">
                <button
                  v-if="i !== 0"
                  class="btn btn-neutral btn-xs rounded border-none mr-2 px-1"
                  @click="clickSort(dialogId, true)"
                >
                  <i class="iconfont icon-up text-xs" />
                </button>
                <button
                  v-if="i !== storySceneList.length - 1"
                  class="btn btn-neutral btn-xs rounded border-none mr-2 px-1"
                  @click="clickSort(dialogId, false)"
                >
                  <i class="iconfont icon-down text-xs" />
                </button>
                <button
                  class="btn btn-neutral btn-xs rounded border-none mr-2 px-1"
                  @click="clickDeleteStoryScene(dialogId)"
                >
                  <i class="iconfont icon-delete text-xs" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <BaseInputModal
      v-model:show="showEditText"
      :text="content"
      type="textarea"
      @confirm="onEditTextConfirm"
    />
    <ModalBgmSelection
      v-model:show="store.showBgmModal"
      :selected-tone-id="store.currentBgmId"
      @update:selectedToneId="onSelectBgm"
    />
    <CreateStorySceneTaskPreview
      v-if="selectedStory"
      :id="selectedStory.storyGameId!"
      ref="previewPopupRef"
      :story="selectedStory!"
      :uuid="selectedStory.storyGameUuid!"
      with-build
    />
    <ModalStoryGameCharacterDialog />

    <story-selection-popup
      v-model:selected-story="selectedStory"
      v-model:show="showStorySelection"
    />
    <tone-selection-popup
      v-model:show="showToneSelection"
      :selected-tone-id="store.mateToneId"
      @update:selected-tone-id="onSelectTone"
    />
  </div>
</template>
